import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { toast } from "sonner";
import NegotiationTable from "@/components/NegotiationTable";
import {
  Plus, Trash2, Package, ShoppingCart, Truck, FileText, CheckCircle, XCircle,
  Clock, Loader2, Send, LogOut, ArrowRight, Eye, ChevronsUpDown, Check,
  IndianRupee, Hash, UserCircle, Edit, RefreshCw, Gift, Shuffle, Sparkles, Star
} from "lucide-react";

const SKU_OPTIONS = ["TIN", "BUCKET", "TIN_REJ", "VACUUM_10", "VACUUM_20"];

const formatIST = (isoStr) => {
  if (!isoStr) return '';
  const d = new Date(isoStr);
  const ist = new Date(d.getTime() + 5.5 * 60 * 60 * 1000);
  const day = String(ist.getUTCDate()).padStart(2, '0');
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${day}-${months[ist.getUTCMonth()]}-${String(ist.getUTCFullYear()).slice(-2)}`;
};

export default function CustomerDashboard({ previewCustomerName, previewDispatchName }) {
  const { user, logout } = useAuth();
  const isPreview = !!previewCustomerName;
  const customerName = previewCustomerName || user?.linked_customer || "";
  const displayName = previewDispatchName || user?.dispatch_name || customerName;

  const [activeTab, setActiveTab] = useState(null); // null = welcome/default screen
  const [orderRequests, setOrderRequests] = useState([]);
  const [myOrders, setMyOrders] = useState([]);
  const [ledgerData, setLedgerData] = useState(null);

  // New order form
  const [items, setItems] = useState([{ grade: "", sku: "BUCKET", qty: "", customer_rate: "", gradeOpen: false }]);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [availableGrades, setAvailableGrades] = useState([]);
  const [availableSkus, setAvailableSkus] = useState([]);
  const [allProducts, setAllProducts] = useState([]);

  // Dispatch detail popup
  const [dispatchPopup, setDispatchPopup] = useState(null);
  // Invoice value breakdown popup
  const [invoicePopup, setInvoicePopup] = useState(null);
  // Deals filter
  const [dealsFilter, setDealsFilter] = useState('all');
  // Market trend rates for auto-suggest
  const [trendRates, setTrendRates] = useState({});
  const [activeTrendName, setActiveTrendName] = useState("regular_trend");
  // Profile popup
  const [profileOpen, setProfileOpen] = useState(false);
  const [profile, setProfile] = useState(null);
  const [editingProfile, setEditingProfile] = useState(false);
  const [editUsername, setEditUsername] = useState("");
  const [editPassword, setEditPassword] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);
  // Customer behaviour profile (rate_preference)
  const [custBehaviourProfile, setCustBehaviourProfile] = useState(null);
  // Highest selling grades (by effective stock)
  const [highStockGrades, setHighStockGrades] = useState([]);

  // Activity tracking helper (fire-and-forget, no await)
  const trackActivity = (action, details = "") => {
    if (isPreview) return; // Don't track admin previews
    axios.post(`${API}/customer/activity`, {
      customer_name: customerName,
      username: user?.username || "",
      action,
      details
    }).catch(() => {});
  };

  // Compute trend rates from market data + optional rate_preference override
  const computeTrendRates = (data, ordersData, ratePref) => {
    const trend = data.active_trend || 'regular_trend';
    setActiveTrendName(trend);
    
    const customerLTP = {};
    for (const o of (ordersData || [])) {
      for (const item of (o.items || [])) {
        const key = `${item.grade}|${item.sku || 'TIN'}`;
        if (item.rate && !customerLTP[key]) {
          customerLTP[key] = item.rate;
        }
      }
    }
    
    const rateMap = {};
    for (const t of (data.trends || [])) {
      const key = `${t.grade}|${t.sku}`;
      const colRate = t.customer_rate || 0;
      const custLastRate = customerLTP[key] || 0;
      
      if (t.customer_rate_na) { rateMap[key] = 0; continue; }
      
      // If customer has a rate_preference set, use BSR/CR rates
      if (ratePref) {
        if (ratePref === 'CR') {
          rateMap[key] = colRate;
        } else if (ratePref === 'BSR-1') {
          rateMap[key] = colRate > 0 ? colRate + 100 : 0;
        } else if (ratePref === 'BSR-2') {
          rateMap[key] = colRate > 0 ? colRate + 200 : 0;
        } else if (ratePref === 'BSR-3') {
          rateMap[key] = colRate > 0 ? colRate + 300 : 0;
        } else {
          rateMap[key] = colRate || t.ltp || 0;
        }
        continue;
      }
      
      if (trend === 'high_trend') {
        rateMap[key] = 0;
      } else if (trend === 'regular_trend') {
        const maxRate = Math.max(colRate, custLastRate);
        rateMap[key] = maxRate > 0 ? maxRate + 100 : 0;
      } else if (trend === 'weak_trend') {
        rateMap[key] = colRate;
      } else if (trend === 'low_trend') {
        rateMap[key] = colRate > 100 ? colRate - 100 : colRate;
      } else {
        rateMap[key] = colRate || t.ltp || 0;
      }
    }
    setTrendRates(rateMap);
  };

  useEffect(() => {
    if (customerName) {
      fetchOrderRequests();
      fetchMyOrders();
      fetchLedger();
      fetchProfile();
      if (!isPreview) trackActivity("login");
    }
    axios.get(`${API}/products`).then(r => {
      const products = r.data || [];
      setAllProducts(products);
      const grades = [...new Set(products.map(p => p.grade).filter(Boolean))].sort();
      const skus = [...new Set(products.map(p => p.sku).filter(Boolean))].sort();
      setAvailableGrades(grades);
      setAvailableSkus(skus.length > 0 ? skus : SKU_OPTIONS);
    }).catch(() => {});

    // Fetch effective stock for highest selling grades
    axios.get(`${API}/products/effective-stock`).then(r => {
      const stockData = r.data || [];
      const sorted = stockData
        .filter(s => s.effective > 0)
        .sort((a, b) => b.effective - a.effective)
        .slice(0, 6);
      setHighStockGrades(sorted);
    }).catch(() => {});

    // Fetch behaviour profile (rate_preference) then market trends
    const fetchBehaviourAndTrends = async () => {
      let ratePref = '';
      try {
        const bpRes = await axios.get(`${API}/customer/behaviour-profile?customer_name=${encodeURIComponent(customerName)}`);
        setCustBehaviourProfile(bpRes.data);
        ratePref = bpRes.data?.rate_preference || '';
      } catch { /* */ }
      
      try {
        const [trendRes, ordersRes] = await Promise.all([
          axios.get(`${API}/market-trends`),
          axios.get(`${API}/customer/my-orders?customer_name=${encodeURIComponent(customerName)}`)
        ]);
        computeTrendRates(trendRes.data || {}, ordersRes.data || [], ratePref);
      } catch { /* */ }
    };
    if (customerName) fetchBehaviourAndTrends();
  }, [customerName]);

  const fetchOrderRequests = async () => {
    try {
      const res = await axios.get(`${API}/customer/order-requests?customer_name=${encodeURIComponent(customerName)}`);
      setOrderRequests(res.data || []);
    } catch (e) { /* */ }
  };

  const fetchMyOrders = async () => {
    try {
      const res = await axios.get(`${API}/customer/my-orders?customer_name=${encodeURIComponent(customerName)}`);
      setMyOrders(res.data || []);
    } catch (e) { /* */ }
  };

  const fetchLedger = async () => {
    try {
      const res = await axios.get(`${API}/ledger/${encodeURIComponent(customerName)}`);
      setLedgerData(res.data);
    } catch (e) { /* */ }
  };

  const fetchProfile = async () => {
    try {
      const res = await axios.get(`${API}/customer/profile?customer_name=${encodeURIComponent(customerName)}`);
      setProfile(res.data);
    } catch (e) { /* */ }
  };

  const saveProfile = async () => {
    if (!profile?.user_id) return;
    setSavingProfile(true);
    try {
      await axios.put(`${API}/customer/update-profile`, {
        user_id: profile.user_id,
        username: editUsername || undefined,
        password: editPassword || undefined
      });
      toast.success("Profile updated!");
      trackActivity(editPassword ? "change_password" : "change_username", `username: ${editUsername}`);
      setEditingProfile(false);
      setEditPassword("");
      fetchProfile();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update");
    } finally {
      setSavingProfile(false);
    }
  };

  const addItem = () => setItems(prev => [...prev, { grade: "", sku: "BUCKET", qty: "", customer_rate: "", gradeOpen: false }]);
  const removeItem = (idx) => {
    setItems(prev => {
      const removed = prev[idx];
      if (removed?.grade) trackActivity("delete_grade", `${removed.grade} ${removed.sku || ''} qty:${removed.qty || 0}`);
      if (prev.length <= 1) return [{ grade: "", sku: "BUCKET", qty: "", customer_rate: "", gradeOpen: false }];
      return prev.filter((_, i) => i !== idx);
    });
  };
  const updateItem = (idx, field, value) => {
    setItems(prev => prev.map((item, i) => i === idx ? { ...item, [field]: value } : item));
  };
  // Track rate/qty on blur (when user finishes typing)
  const trackFieldChange = (idx, field) => {
    const item = items[idx];
    if (!item?.grade) return;
    if (field === 'qty' && item.qty) {
      trackActivity("edit_qty", `${item.grade} qty:${item.qty}`);
      // Track rate viewed when qty is set and suggested rate is visible
      const rate = getSuggestedRate(item.grade, item.sku);
      if (parseInt(item.qty) > 0 && rate > 0) {
        trackActivity("view_rate", `${item.grade} ${item.sku} qty:${item.qty} rate:₹${rate}`);
      }
    }
    if (field === 'customer_rate' && item.customer_rate) trackActivity("edit_rate", `${item.grade} rate:${item.customer_rate}`);
  };

  // Get suggested rate for a grade+sku from market trends
  const getSuggestedRate = (grade, sku) => {
    if (!grade) return 0;
    const key = `${grade}|${sku}`;
    return trendRates[key] || trendRates[`${grade}|BUCKET`] || trendRates[`${grade}|TIN`] || 0;
  };

  const submitOrderRequest = async () => {
    const validItems = items.filter(i => i.grade && parseInt(i.qty) > 0);
    if (validItems.length === 0) { toast.error("Add at least one grade with qty"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/customer/order-request`, {
        customer_name: customerName,
        customer_id: user?.linked_customer_id || "",
        items: validItems.map(i => ({
          grade: i.grade.toUpperCase().trim(),
          sku: i.sku,
          qty: parseInt(i.qty),
          customer_rate: i.customer_rate ? parseFloat(i.customer_rate) : 0,
          suggested_rate: getSuggestedRate(i.grade, i.sku)
        })),
        notes
      });
      toast.success("Order request submitted!");
      trackActivity("submit_request", validItems.map(i => i.grade.toUpperCase().trim()).join(", "));
      setItems([{ grade: "", sku: "BUCKET", qty: "", customer_rate: "", gradeOpen: false }]);
      setNotes("");
      fetchOrderRequests();
      setActiveTab("requests");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to submit");
    } finally {
      setSubmitting(false);
    }
  };

  // Compute most frequently ordered grades from order history
  const frequentGrades = (() => {
    const gradeSkuCount = {};
    for (const o of myOrders) {
      for (const i of (o.items || [])) {
        const g = i.grade;
        const s = i.sku || 'BUCKET';
        if (!g || g.startsWith('TEST_')) continue;
        const key = `${g}|${s}`;
        gradeSkuCount[key] = (gradeSkuCount[key] || 0) + (i.qty || 1);
      }
    }
    return Object.entries(gradeSkuCount)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6)
      .map(([key]) => {
        const [grade, sku] = key.split('|');
        return { grade, sku };
      });
  })();

  // Requests: only active negotiations (not confirmed/ordered/rejected/cancelled)
  const activeRequests = orderRequests.filter(r => 
    ['pending', 'negotiating', 'countered'].includes(r.status)
  );
  const pendingActionCount = orderRequests.filter(r => ['negotiating', 'countered'].includes(r.status)).length;

  // Order status helpers
  const getOrderStatusInfo = (order) => {
    const status = order.status || '';
    const payTerms = (order.payment_terms || '').toUpperCase();
    const needsPay = ['ADV', 'RDAP'].includes(payTerms);

    if (status === 'dispatched') return { label: 'Dispatched', cls: 'bg-green-100 text-green-800', icon: <Truck className="w-3 h-3" /> };
    if (status === 'cancelled') return { label: 'Order Cancelled', cls: 'bg-red-100 text-red-800', icon: <XCircle className="w-3 h-3" /> };
    if (status === 'ready_to_dispatch') return { label: 'Ready to Dispatch', cls: 'bg-purple-100 text-purple-800', icon: <Package className="w-3 h-3" /> };
    if (status === 'to_sort') return { label: 'To Sort', cls: 'bg-indigo-100 text-indigo-800', icon: <Package className="w-3 h-3" /> };
    if (status === 'in_process') return { label: 'In Process', cls: 'bg-cyan-100 text-cyan-800', icon: <Clock className="w-3 h-3" /> };
    if (status === 'confirmed' && needsPay) return { label: 'Request to Pay', cls: 'bg-amber-100 text-amber-800', icon: <IndianRupee className="w-3 h-3" /> };
    if (status === 'confirmed') return { label: 'Order Confirmed', cls: 'bg-blue-100 text-blue-800', icon: <CheckCircle className="w-3 h-3" /> };
    return { label: (status || 'Unknown').replace(/_/g, ' '), cls: 'bg-gray-100 text-gray-700', icon: null };
  };

  // Get display value — use total_value - total_discount for discounted orders
  const getOrderDisplayValue = (order) => {
    const payTerms = (order.payment_terms || '').toUpperCase();
    if (['ADV', 'RDAP', 'DDAP', 'DDAP RECVD'].includes(payTerms) && order.total_discount > 0) {
      return (order.total_value || 0) - (order.total_discount || 0);
    }
    return order.total_value || 0;
  };

  // Ledger: filter to last 2 months only, compute opening balance
  const [refreshing, setRefreshing] = useState(false);
  const refreshTab = async () => {
    setRefreshing(true);
    try {
      if (activeTab === 'requests') await fetchOrderRequests();
      else if (activeTab === 'orders') await fetchMyOrders();
      else if (activeTab === 'ledger') await fetchLedger();
      else if (activeTab === 'new_order') {
        let ratePref = custBehaviourProfile?.rate_preference || '';
        try {
          const bpRes = await axios.get(`${API}/customer/behaviour-profile?customer_name=${encodeURIComponent(customerName)}`);
          setCustBehaviourProfile(bpRes.data);
          ratePref = bpRes.data?.rate_preference || '';
        } catch { /* */ }
        const [trendRes, ordersRes] = await Promise.all([
          axios.get(`${API}/market-trends`),
          axios.get(`${API}/customer/my-orders?customer_name=${encodeURIComponent(customerName)}`)
        ]);
        computeTrendRates(trendRes.data || {}, ordersRes.data || [], ratePref);
        // Refresh effective stock too
        try {
          const stockRes = await axios.get(`${API}/products/effective-stock`);
          const sorted = (stockRes.data || []).filter(s => s.effective > 0).sort((a, b) => b.effective - a.effective).slice(0, 6);
          setHighStockGrades(sorted);
        } catch { /* */ }
      }
      toast.success("Refreshed!");
    } catch (e) { /* */ }
    finally { setTimeout(() => setRefreshing(false), 500); }
  };

  const getLedger2Months = () => {
    if (!ledgerData?.entries?.length) return { entries: [], openingBalance: 0, closingBalance: ledgerData?.closing_balance || 0 };

    const entries = ledgerData.entries;
    // Get unique months from entries, sorted descending
    const months = [...new Set(entries.map(e => (e.date || '').slice(0, 7)).filter(Boolean))].sort().reverse();
    const last2Months = months.slice(0, 2);

    if (last2Months.length === 0) return { entries: [], openingBalance: 0, closingBalance: ledgerData.closing_balance || 0 };

    const cutoffMonth = last2Months[last2Months.length - 1]; // earliest of the 2 months
    const filteredEntries = [];
    let openingBalance = 0;

    for (const e of entries) {
      const entryMonth = (e.date || '').slice(0, 7);
      if (entryMonth < cutoffMonth) {
        // Before our 2-month window — this contributes to opening balance
        openingBalance = e.balance;
      } else if (last2Months.includes(entryMonth)) {
        filteredEntries.push(e);
      }
    }

    return {
      entries: filteredEntries,
      openingBalance,
      closingBalance: ledgerData.closing_balance || 0,
      months: last2Months
    };
  };

  const tabColors = {
    new_order: { active: 'bg-emerald-600 text-white', inactive: 'bg-emerald-100 text-emerald-800 hover:bg-emerald-200' },
    requests: { active: 'bg-amber-500 text-white', inactive: 'bg-amber-100 text-amber-800 hover:bg-amber-200' },
    orders: { active: 'bg-blue-600 text-white', inactive: 'bg-blue-100 text-blue-800 hover:bg-blue-200' },
    ledger: { active: 'bg-purple-600 text-white', inactive: 'bg-purple-100 text-purple-800 hover:bg-purple-200' },
    deals: { active: 'bg-teal-600 text-white', inactive: 'bg-teal-100 text-teal-800 hover:bg-teal-200' },
  };

  const tabs = [
    { id: "new_order", label: "New Order", icon: <Plus className="w-4 h-4" /> },
    { id: "requests", label: "Requests", icon: <Send className="w-4 h-4" />, count: pendingActionCount },
    { id: "orders", label: "Orders", icon: <ShoppingCart className="w-4 h-4" />, count: myOrders.length },
    { id: "ledger", label: "Ledger", icon: <FileText className="w-4 h-4" /> },
    { id: "deals", label: "Deals", icon: <CheckCircle className="w-4 h-4" /> },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b px-4 py-3 flex items-center justify-between sticky top-0 z-10">
        <div className="flex items-center gap-2 min-w-0 cursor-pointer" onClick={() => setActiveTab(null)}>
          <img src="/purelife-logo.png" alt="Purelife" className="w-8 h-8 rounded-full object-cover flex-shrink-0" />
          <div className="min-w-0">
            <h1 className="text-sm sm:text-base font-bold truncate" style={{ fontFamily: 'Manrope' }}>
              {isPreview && <Eye className="w-3.5 h-3.5 inline mr-1 text-blue-500" />}
              {displayName}
            </h1>
            <p className="text-[10px] text-muted-foreground">{isPreview ? 'Admin Preview' : 'Customer Portal'}</p>
          </div>
        </div>
        {!isPreview && (
          <div className="flex items-center gap-2 flex-shrink-0">
            <button onClick={() => { setProfileOpen(true); fetchProfile(); trackActivity("view_profile"); }}
              className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
              data-testid="profile-icon">
              <UserCircle className="w-5 h-5 text-gray-600" />
            </button>
            <Button variant="outline" size="sm" onClick={logout} className="gap-1 h-8 text-xs">
              <LogOut className="w-3.5 h-3.5" /> Logout
            </Button>
          </div>
        )}
      </div>

      {/* Tab bar — colored buttons with refresh */}
      <div className="bg-white border-b px-2 py-1.5 overflow-x-auto flex gap-1.5 items-center sticky top-[52px] z-10">
        {tabs.map(t => (
          <Button key={t.id} size="sm"
            onClick={() => {
              setActiveTab(t.id);
              if (t.id === 'orders') trackActivity("view_orders");
              else if (t.id === 'ledger') trackActivity("view_ledger");
              else if (t.id === 'requests') trackActivity("view_requests");
              else if (t.id === 'new_order') trackActivity("view_new_order");
            }}
            className={`h-9 px-3 text-xs gap-1.5 whitespace-nowrap flex-shrink-0 font-semibold rounded-full ${
              activeTab === t.id ? tabColors[t.id].active : tabColors[t.id].inactive
            }`}
            data-testid={`customer-tab-${t.id}`}>
            {t.icon} {t.label}
            {t.count > 0 && <Badge className="ml-0.5 text-[9px] px-1.5 h-4 bg-white/20 text-inherit">{t.count}</Badge>}
          </Button>
        ))}
        {/* Refresh button — appears when a tab is active */}
        {activeTab && (
          <button onClick={() => { refreshTab(); trackActivity("click_refresh", activeTab); }} disabled={refreshing}
            className="ml-auto w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors flex-shrink-0"
            data-testid="refresh-btn">
            <RefreshCw className={`w-3.5 h-3.5 text-gray-600 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        )}
      </div>

      <div className="px-3 sm:px-6 py-4 pb-20 max-w-2xl mx-auto">

        {/* Default Welcome Screen — Logo shifted 20% upward */}
        {activeTab === null && (
          <div className="flex flex-col items-center" style={{ minHeight: 'calc(100vh - 140px)', paddingTop: 'calc((100vh - 140px) * 0.15)' }} data-testid="welcome-screen">
            <img src="/purelife-logo.png" alt="Purelife" className="rounded-full object-cover" style={{ width: 'min(75vw, 320px)', height: 'min(75vw, 320px)' }} />
            <h2 className="text-xl sm:text-2xl font-extrabold text-gray-900 mt-6 text-center" style={{ fontFamily: 'Manrope' }}>
              Welcome to Purelife Bot
            </h2>
            <p className="text-base font-bold text-muted-foreground mt-1">B2B Sales Portal</p>
          </div>
        )}

        {/* New Order Tab */}
        {activeTab === "new_order" && (
          <div className="space-y-4" data-testid="new-order-card">
            <CustomerComboOffer
              customerName={customerName}
              customerId={user?.linked_customer_id || ""}
              ownerView={isPreview || ["owner", "admin"].includes(user?.role)}
              onSubmitted={() => { fetchOrderRequests(); setActiveTab("requests"); }}
            />
            {/* Credit Limit Warning */}
            {(() => {
              const closingBal = ledgerData?.closing_balance || 0;
              const creditLimit = custBehaviourProfile?.credit_limit ?? 2000000;
              if (closingBal > creditLimit) {
                return (
                  <div className="bg-red-50 border-2 border-red-300 rounded-xl p-4 text-center" data-testid="credit-limit-warning">
                    <p className="text-sm font-bold text-red-700 mb-1">Service Paused</p>
                    <p className="text-xs text-red-600">
                      Kindly clear your outstanding amount of <span className="font-bold">₹{closingBal.toLocaleString('en-IN')}</span> to resume services.
                    </p>
                  </div>
                );
              }
              return null;
            })()}
            {/* Only show order form when credit limit not exceeded */}
            {(ledgerData?.closing_balance || 0) <= (custBehaviourProfile?.credit_limit ?? 2000000) && (
            <div className="bg-white rounded-xl border p-4">
              <h2 className="text-sm font-extrabold mb-1" style={{ fontFamily: 'Manrope' }}>Place Order Request</h2>
              <p className="text-[11px] text-muted-foreground mb-3">Add grades & qty. You can suggest rates.</p>

              {/* Highest Selling Grades (by effective stock) — only if profile preference is set */}
              {highStockGrades.length > 0 && custBehaviourProfile?.show_highest_selling && (
                <div className="mb-3" data-testid="highest-selling-grades">
                  <p className="text-[10px] font-bold text-orange-700 mb-1.5">HIGHEST SELLING GRADES</p>
                  <div className="flex flex-wrap gap-1.5">
                    {highStockGrades.map(hg => (
                      <button key={`${hg.grade}-${hg.sku}`}
                        onClick={() => {
                          trackActivity("click_high_stock_grade", `${hg.grade} ${hg.sku}`);
                          setItems(prev => {
                            const emptyIdx = prev.findIndex(it => !it.grade);
                            if (emptyIdx >= 0) {
                              return prev.map((it, i) => i === emptyIdx ? { ...it, grade: hg.grade, sku: hg.sku } : it);
                            }
                            return [...prev, { grade: hg.grade, sku: hg.sku, qty: "", customer_rate: "", gradeOpen: false }];
                          });
                        }}
                        className="px-2.5 py-1.5 bg-orange-50 hover:bg-orange-100 border border-orange-200 rounded-lg text-[11px] font-semibold text-orange-800 transition-colors"
                        data-testid={`high-stock-grade-${hg.grade}`}
                      >
                        {hg.grade} <span className="text-[9px] font-normal text-orange-600 ml-0.5">{hg.sku}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Discounted Grades — only if profile preference is set */}
              {custBehaviourProfile?.show_discounted_grades && (custBehaviourProfile.discounted_grades || []).length > 0 && (
                <div className="mb-3" data-testid="discounted-grades-section">
                  <p className="text-[10px] font-bold text-rose-700 mb-1.5">DISCOUNTED GRADES</p>
                  <div className="flex flex-wrap gap-1.5">
                    {(custBehaviourProfile.discounted_grades || []).filter(dg => dg.grade && dg.offered_rate).map((dg, idx) => (
                      <button key={`disc-${dg.grade}-${dg.sku}-${idx}`}
                        onClick={() => {
                          trackActivity("click_discounted_grade", `${dg.grade} ${dg.sku} offered:${dg.offered_rate}`);
                          setItems(prev => {
                            const emptyIdx = prev.findIndex(it => !it.grade);
                            if (emptyIdx >= 0) {
                              return prev.map((it, i) => i === emptyIdx ? { ...it, grade: dg.grade, sku: dg.sku, customer_rate: String(dg.offered_rate) } : it);
                            }
                            return [...prev, { grade: dg.grade, sku: dg.sku, qty: "", customer_rate: String(dg.offered_rate), gradeOpen: false }];
                          });
                        }}
                        className="px-2.5 py-1.5 bg-rose-50 hover:bg-rose-100 border border-rose-200 rounded-lg text-[11px] font-semibold text-rose-800 transition-colors"
                        data-testid={`discounted-grade-${dg.grade}`}
                      >
                        {dg.grade} <span className="text-[9px] font-normal text-rose-600 ml-0.5">{dg.sku}</span>
                        {dg.original_rate > 0 && (
                          <span className="text-[9px] text-gray-400 line-through ml-1">₹{dg.original_rate}</span>
                        )}
                        <span className="text-[9px] font-bold text-green-700 ml-1">₹{dg.offered_rate}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Frequent grades quick-select buttons */}
              {frequentGrades.length > 0 && (
                <div className="mb-3">
                  <p className="text-[10px] font-bold text-muted-foreground mb-1.5">Your Frequent Grades</p>
                  <div className="flex flex-wrap gap-1.5">
                    {frequentGrades.map(fg => (
                      <button key={`${fg.grade}-${fg.sku}`}
                        onClick={() => {
                          trackActivity("click_freq_grade", `${fg.grade} ${fg.sku}`);
                          setItems(prev => {
                            const emptyIdx = prev.findIndex(it => !it.grade);
                            if (emptyIdx >= 0) {
                              return prev.map((it, i) => i === emptyIdx ? { ...it, grade: fg.grade, sku: fg.sku } : it);
                            }
                            return [...prev, { grade: fg.grade, sku: fg.sku, qty: "", customer_rate: "", gradeOpen: false }];
                          });
                        }}
                        className="px-2.5 py-1.5 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded-lg text-[11px] font-semibold text-emerald-800 transition-colors"
                        data-testid={`freq-grade-${fg.grade}`}
                      >
                        {fg.grade} <span className="text-[9px] font-normal text-emerald-600 ml-0.5">{fg.sku}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="overflow-x-auto mb-3">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b">
                      <th className="text-left py-2 px-2 text-[10px] font-extrabold text-gray-700 w-[24%]">Grade</th>
                      <th className="text-left py-2 px-2 text-[10px] font-extrabold text-gray-700 w-[14%]">SKU</th>
                      <th className="text-center py-2 px-2 text-[10px] font-extrabold text-gray-700 w-[14%]">Qty *</th>
                      <th className="text-center py-2 px-2 text-[10px] font-extrabold text-gray-700 w-[18%]">Rate</th>
                      <th className="text-center py-2 px-2 text-[10px] font-extrabold text-gray-700 w-[22%]">Your Rate</th>
                      <th className="w-[8%]"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, idx) => {
                      const suggestedRate = getSuggestedRate(item.grade, item.sku);
                      const showSuggested = item.grade && parseInt(item.qty) > 0 && suggestedRate > 0;
                      return (
                      <tr key={idx} className="border-b" data-testid={`order-item-${idx}`}>
                        <td className="py-1.5 px-1">
                          <Popover open={item.gradeOpen} onOpenChange={(open) => updateItem(idx, "gradeOpen", open)}>
                            <PopoverTrigger asChild>
                              <Button variant="outline" role="combobox" aria-expanded={item.gradeOpen}
                                className="h-9 w-full justify-between text-xs border-0 bg-transparent px-1 font-normal"
                                data-testid={`grade-select-${idx}`}>
                                <span className={item.grade ? "text-foreground font-medium" : "text-muted-foreground"}>
                                  {item.grade || "Grade"}
                                </span>
                                <ChevronsUpDown className="ml-1 h-3 w-3 shrink-0 opacity-50" />
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-[200px] p-0" align="start">
                              <Command>
                                <CommandInput placeholder="Search grade..." className="h-8 text-xs" />
                                <CommandList>
                                  <CommandEmpty className="py-2 text-center text-xs text-muted-foreground">No grade found</CommandEmpty>
                                  <CommandGroup className="max-h-[200px] overflow-y-auto">
                                    {availableGrades.map(g => (
                                      <CommandItem key={g} value={g} onSelect={() => {
                                        updateItem(idx, "grade", g);
                                        updateItem(idx, "gradeOpen", false);
                                        trackActivity("search_grade", g);
                                        // Track rate viewed if qty already exists
                                        const existingQty = parseInt(items[idx]?.qty) || 0;
                                        if (existingQty > 0) {
                                          const rate = getSuggestedRate(g, items[idx]?.sku || 'BUCKET');
                                          if (rate > 0) trackActivity("view_rate", `${g} ${items[idx]?.sku || 'BUCKET'} qty:${existingQty} rate:₹${rate}`);
                                        }
                                      }} className="text-xs cursor-pointer">
                                        <Check className={`mr-1.5 h-3 w-3 ${item.grade === g ? "opacity-100" : "opacity-0"}`} />
                                        {g}
                                      </CommandItem>
                                    ))}
                                  </CommandGroup>
                                </CommandList>
                              </Command>
                            </PopoverContent>
                          </Popover>
                        </td>
                        <td className="py-1.5 px-1">
                          <Select value={item.sku} onValueChange={v => updateItem(idx, "sku", v)}>
                            <SelectTrigger className="h-9 text-xs border-0 bg-transparent p-1"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="BUCKET">BUCKET</SelectItem>
                              <SelectItem value="TIN">TIN</SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="py-1.5 px-1">
                          <Input type="number" value={item.qty} onChange={e => updateItem(idx, "qty", e.target.value)}
                            onBlur={() => trackFieldChange(idx, "qty")}
                            placeholder="0" className="h-9 text-sm text-center border-0 bg-transparent p-1 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" min={0} />
                        </td>
                        {/* Suggested Rate — auto-filled from market trends, or message for high trend */}
                        <td className="py-1.5 px-1 text-center">
                          {item.grade && parseInt(item.qty) > 0 ? (
                            activeTrendName === 'high_trend' ? (
                              <span className="text-[9px] text-red-600 font-semibold leading-tight block px-1">
                                High demand! Enter your best buy rate
                              </span>
                            ) : suggestedRate > 0 ? (
                              <span className="text-xs font-mono font-semibold text-blue-700 bg-blue-50 px-2 py-1 rounded">
                                ₹{suggestedRate.toLocaleString('en-IN')}
                              </span>
                            ) : (
                              <span className="text-[9px] text-muted-foreground italic">NA</span>
                            )
                          ) : null}
                        </td>
                        {/* Your Rate — customer can counter */}
                        <td className="py-1.5 px-1">
                          {item.grade && (parseInt(item.qty) > 0 || item.customer_rate) ? (
                            <Input type="number" value={item.customer_rate} onChange={e => updateItem(idx, "customer_rate", e.target.value)}
                              onBlur={() => trackFieldChange(idx, "customer_rate")}
                              placeholder={activeTrendName === 'high_trend' ? "Best Rate ₹" : "Counter ₹"}
                              className="h-9 text-sm text-center border border-amber-200 bg-amber-50/50 rounded p-1 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" min={0} />
                          ) : null}
                        </td>
                        <td className="py-1.5 px-0 text-center">
                          <button onClick={() => removeItem(idx)} className="text-red-400 hover:text-red-600 p-1">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </td>
                      </tr>
                    );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="bg-gray-50">
                      <td colSpan={2} className="py-2 px-2 text-[10px] font-bold text-right">Total Qty:</td>
                      <td className="py-2 px-2 text-sm text-center font-bold">{items.reduce((s, i) => s + (parseInt(i.qty) || 0), 0)}</td>
                      <td className="py-2 px-2 text-[10px] text-center text-muted-foreground">
                        {items.reduce((s, i) => {
                          const q = parseInt(i.qty) || 0;
                          const r = getSuggestedRate(i.grade, i.sku);
                          return s + (q * r);
                        }, 0) > 0 && `₹${items.reduce((s, i) => s + ((parseInt(i.qty) || 0) * getSuggestedRate(i.grade, i.sku)), 0).toLocaleString('en-IN')}`}
                      </td>
                      <td className="py-2 px-2 text-[10px] text-center text-amber-700">
                        {items.reduce((s, i) => s + ((parseInt(i.qty) || 0) * (parseFloat(i.customer_rate) || 0)), 0) > 0 &&
                          `₹${items.reduce((s, i) => s + ((parseInt(i.qty) || 0) * (parseFloat(i.customer_rate) || 0)), 0).toLocaleString('en-IN')}`
                        }
                      </td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
              
              <Button variant="outline" size="sm" onClick={addItem} className="gap-1 w-full h-9 mb-3 border-dashed">
                <Plus className="w-3.5 h-3.5" /> Add Row
              </Button>
              
              <Textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Notes (optional)..."
                rows={2} className="text-sm mb-3" />
              
              <Button onClick={submitOrderRequest} disabled={submitting || isPreview}
                className="gap-2 w-full h-11 text-sm font-semibold" data-testid="submit-order-request">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Submit Order Request
              </Button>
            </div>
            )}
          </div>
        )}

        {/* Requests Tab */}
        {activeTab === "requests" && (
          <div className="space-y-3" data-testid="order-requests-card">
            {activeRequests.length > 0 ? activeRequests.map(req => (
              <NegotiationTable
                key={req.id}
                request={req}
                viewerRole="customer"
                onUpdate={fetchOrderRequests}
                isPreview={isPreview}
                products={allProducts}
              />
            )) : (
              <div className="bg-white rounded-xl border p-8 text-center">
                <Send className="w-8 h-8 text-muted-foreground/30 mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">No active requests</p>
                <Button variant="outline" size="sm" className="mt-3 gap-1" onClick={() => setActiveTab("new_order")}>
                  <Plus className="w-3.5 h-3.5" /> Place New Order
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Orders Tab — Tabular */}
        {activeTab === "orders" && (
          <div data-testid="my-orders-card">
            {myOrders.length > 0 ? (
              <div className="bg-white rounded-xl border overflow-hidden">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead className="text-[10px] py-2 px-2 font-extrabold">Order #</TableHead>
                        <TableHead className="text-[10px] py-2 px-2 font-extrabold">Items</TableHead>
                        <TableHead className="text-[10px] py-2 px-2 font-extrabold text-center">Qty</TableHead>
                        <TableHead className="text-[10px] py-2 px-2 font-extrabold text-right">Value</TableHead>
                        <TableHead className="text-[10px] py-2 px-2 font-extrabold text-center">Status</TableHead>
                        <TableHead className="text-[10px] py-2 px-2 font-extrabold text-center">Invoice</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {myOrders.map(o => {
                        const statusInfo = getOrderStatusInfo(o);
                        const payTerms = (o.payment_terms || '').toUpperCase();
                        const needsPay = ['ADV', 'RDAP'].includes(payTerms) && o.status === 'confirmed';
                        return (
                          <TableRow key={o.id} className="hover:bg-gray-50/50" data-testid={`order-row-${o.id}`}>
                            <TableCell className="py-2 px-2">
                              <span className="text-xs font-mono font-bold block">{o.order_number}</span>
                              <span className="text-[9px] text-muted-foreground">{o.order_date?.slice(0, 10)}</span>
                            </TableCell>
                            <TableCell className="py-2 px-2">
                              <div className="flex flex-wrap gap-0.5">
                                {o.items?.map((i, idx) => (
                                  <span key={idx} className="bg-gray-100 px-1.5 py-0.5 rounded text-[9px]">
                                    {i.grade} {i.sku !== 'TIN' ? i.sku : ''} x{i.qty}
                                  </span>
                                ))}
                              </div>
                              {o.items?.length > 0 && (
                                <span className="text-[9px] text-muted-foreground">
                                  @₹{o.items[0].rate?.toLocaleString('en-IN')}{o.items.length > 1 ? '...' : ''}
                                </span>
                              )}
                            </TableCell>
                            <TableCell className="py-2 px-2 text-center text-xs font-bold">{o.total_qty}</TableCell>
                            <TableCell className="py-2 px-2 text-right text-xs font-semibold text-green-700 cursor-pointer hover:bg-green-50 transition-colors"
                              onClick={() => { setInvoicePopup(o); trackActivity("view_invoice", o.order_number); }}>
                              ₹{getOrderDisplayValue(o).toLocaleString('en-IN')}
                              {o.total_discount > 0 && (
                                <span className="block text-[8px] text-muted-foreground line-through">₹{(o.total_value || 0).toLocaleString('en-IN')}</span>
                              )}
                            </TableCell>
                            <TableCell className="py-2 px-2 text-center">
                              <Badge className={`${statusInfo.cls} text-[9px] gap-0.5`}>
                                {statusInfo.icon}{statusInfo.label}
                              </Badge>
                              {needsPay && (
                                <div className="mt-1">
                                  <span className="text-[8px] text-amber-700 font-semibold bg-amber-50 px-1.5 py-0.5 rounded">
                                    {payTerms} — Pay to proceed
                                  </span>
                                </div>
                              )}
                              {o.dispatch_number && (
                                <button
                                  onClick={() => { setDispatchPopup(o); trackActivity("view_dispatch", o.dispatch_number); }}
                                  className="mt-1 block mx-auto text-[9px] font-mono text-emerald-700 hover:text-emerald-900 underline"
                                  data-testid={`dispatch-link-${o.id}`}
                                >
                                  {o.dispatch_number}
                                </button>
                              )}
                            </TableCell>
                            {/* Invoice PDF column */}
                            <TableCell className="py-2 px-2 text-center">
                              {o.invoice_pdf_path && o.dispatch_number ? (
                                <button onClick={() => window.open(`${API}/dispatch/${o.dispatch_number}/invoice`, '_blank')}
                                  className="text-blue-600 hover:text-blue-800 mx-auto flex items-center justify-center gap-0.5 text-[9px]" title="View & Download Invoice">
                                  <FileText className="w-3.5 h-3.5" />
                                </button>
                              ) : (
                                <span className="text-[9px] text-muted-foreground">-</span>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-xl border p-8 text-center">
                <ShoppingCart className="w-8 h-8 text-muted-foreground/30 mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">No orders yet</p>
              </div>
            )}
          </div>
        )}

        {/* Ledger Tab — Last 2 months with opening/closing */}
        {activeTab === "ledger" && (() => {
          const { entries, openingBalance, closingBalance, months } = getLedger2Months();
          return (
            <div data-testid="customer-ledger-card">
              {/* Opening & Closing Balance Cards */}
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div className="bg-white rounded-xl border p-3">
                  <p className="text-[10px] text-muted-foreground mb-0.5">Opening Balance</p>
                  <p className={`text-lg font-bold ${openingBalance > 0 ? 'text-red-600' : openingBalance < 0 ? 'text-green-600' : ''}`}>
                    ₹{Math.abs(Math.round(openingBalance)).toLocaleString('en-IN')}
                    <span className="text-xs ml-1">{openingBalance < 0 ? 'CR' : openingBalance > 0 ? 'DR' : ''}</span>
                  </p>
                </div>
                <div className="bg-white rounded-xl border p-3">
                  <p className="text-[10px] text-muted-foreground mb-0.5">Closing Balance</p>
                  <p className={`text-lg font-bold ${closingBalance > 0 ? 'text-red-600' : closingBalance < 0 ? 'text-green-600' : ''}`}>
                    ₹{Math.abs(Math.round(closingBalance)).toLocaleString('en-IN')}
                    <span className="text-xs ml-1">{closingBalance < 0 ? 'CR' : closingBalance > 0 ? 'DR' : ''}</span>
                  </p>
                </div>
              </div>

              {months?.length > 0 && (
                <p className="text-[10px] text-muted-foreground mb-2">
                  Showing: {months.slice().reverse().join(', ')}
                </p>
              )}

              {entries.length > 0 ? (
                <div className="bg-white rounded-xl border overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead className="text-[10px] py-1.5 px-2 font-extrabold">Date</TableHead>
                        <TableHead className="text-[10px] py-1.5 px-2 font-extrabold">Description</TableHead>
                        <TableHead className="text-[10px] py-1.5 px-2 font-extrabold text-right">Debit</TableHead>
                        <TableHead className="text-[10px] py-1.5 px-2 font-extrabold text-right">Credit</TableHead>
                        <TableHead className="text-[10px] py-1.5 px-2 font-extrabold text-right">Balance</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {/* Opening balance row */}
                      {openingBalance !== 0 && (
                        <TableRow className="bg-blue-50/50">
                          <TableCell className="text-[10px] py-1.5 px-2 font-semibold" colSpan={4}>Opening Balance</TableCell>
                          <TableCell className={`text-[10px] py-1.5 px-2 text-right font-bold ${openingBalance > 0 ? 'text-red-600' : 'text-green-600'}`}>
                            ₹{Math.abs(Math.round(openingBalance)).toLocaleString('en-IN')}{openingBalance < 0 ? ' CR' : ''}
                          </TableCell>
                        </TableRow>
                      )}
                      {entries.map((e, i) => (
                        <TableRow key={i}>
                          <TableCell className="text-[10px] py-1.5 px-2 whitespace-nowrap">{e.date}</TableCell>
                          <TableCell className="text-[10px] py-1.5 px-2">
                            <span>{e.description}</span>
                            {e.dispatch_number && (
                              <button
                                onClick={() => {
                                  const matchOrder = myOrders.find(o => o.dispatch_number === e.dispatch_number);
                                  if (matchOrder) { setDispatchPopup(matchOrder); trackActivity("view_dispatch", e.dispatch_number); }
                                  else toast.info(`Dispatch: ${e.dispatch_number}`);
                                }}
                                className="ml-1 text-[9px] font-mono text-emerald-700 hover:text-emerald-900 underline"
                              >
                                {e.dispatch_number}
                              </button>
                            )}
                          </TableCell>
                          <TableCell className="text-[10px] py-1.5 px-2 text-right text-red-600 font-semibold">
                            {e.debit > 0 ? `₹${e.debit.toLocaleString('en-IN')}` : ''}
                          </TableCell>
                          <TableCell className="text-[10px] py-1.5 px-2 text-right text-green-600 font-semibold">
                            {e.credit > 0 ? `₹${e.credit.toLocaleString('en-IN')}` : ''}
                          </TableCell>
                          <TableCell className="text-[10px] py-1.5 px-2 text-right font-semibold">
                            ₹{Math.abs(Math.round(e.balance)).toLocaleString('en-IN')}{e.balance < 0 ? ' CR' : ''}
                          </TableCell>
                        </TableRow>
                      ))}
                      {/* Closing balance row */}
                      <TableRow className="bg-gray-100">
                        <TableCell className="text-[10px] py-1.5 px-2 font-bold" colSpan={4}>Closing Balance</TableCell>
                        <TableCell className={`text-[10px] py-1.5 px-2 text-right font-bold ${closingBalance > 0 ? 'text-red-600' : closingBalance < 0 ? 'text-green-600' : ''}`}>
                          ₹{Math.abs(Math.round(closingBalance)).toLocaleString('en-IN')}{closingBalance < 0 ? ' CR' : ''}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="bg-white rounded-xl border p-8 text-center">
                  <FileText className="w-8 h-8 text-muted-foreground/30 mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">No ledger entries</p>
                </div>
              )}
            </div>
          );
        })()}
      </div>



      {/* Customer Profile Popup */}
      <Dialog open={profileOpen} onOpenChange={setProfileOpen}>
        <DialogContent className="sm:max-w-[380px]" data-testid="profile-popup">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base" style={{ fontFamily: 'Manrope' }}>
              <UserCircle className="w-5 h-5 text-blue-600" />
              My Profile
            </DialogTitle>
          </DialogHeader>
          {profile ? (
            <div className="space-y-3 mt-2">
              <div className="space-y-2.5">
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-[10px] text-muted-foreground mb-0.5 font-semibold">Billing Name</p>
                  <p className="text-sm font-bold">{profile.billing_name}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-[10px] text-muted-foreground mb-0.5 font-semibold">Contact Number</p>
                  <p className="text-sm font-medium">{profile.contact_number || '-'}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-[10px] text-muted-foreground mb-0.5 font-semibold">GSTIN Number</p>
                  <p className="text-sm font-mono font-medium">{profile.gstin || '-'}</p>
                </div>
              </div>

              <div className="border-t pt-3 space-y-2.5">
                {editingProfile ? (
                  <>
                    <div>
                      <Label className="text-[10px] font-semibold text-muted-foreground">Username</Label>
                      <Input value={editUsername} onChange={e => setEditUsername(e.target.value)}
                        className="h-8 text-sm mt-0.5" data-testid="edit-username" />
                    </div>
                    <div>
                      <Label className="text-[10px] font-semibold text-muted-foreground">New Password</Label>
                      <Input value={editPassword} onChange={e => setEditPassword(e.target.value)}
                        placeholder="Leave blank to keep current" className="h-8 text-sm mt-0.5" data-testid="edit-password" />
                    </div>
                    <div className="flex gap-2 pt-1">
                      <Button size="sm" onClick={saveProfile} disabled={savingProfile} className="gap-1 text-xs h-8">
                        {savingProfile ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                        Save
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setEditingProfile(false)} className="text-xs h-8">Cancel</Button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="bg-blue-50 rounded-lg p-3">
                      <p className="text-[10px] text-muted-foreground mb-0.5 font-semibold">Username</p>
                      <p className="text-sm font-mono font-medium">{profile.username}</p>
                    </div>
                    <div className="bg-blue-50 rounded-lg p-3">
                      <p className="text-[10px] text-muted-foreground mb-0.5 font-semibold">Password</p>
                      <p className="text-sm font-mono font-medium">{profile.password ? '••••••••' : '-'}</p>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => {
                      setEditUsername(profile.username);
                      setEditPassword("");
                      setEditingProfile(true);
                    }} className="gap-1 text-xs h-8 w-full" data-testid="edit-profile-btn">
                      <Edit className="w-3 h-3" /> Change Username / Password
                    </Button>
                  </>
                )}
              </div>
            </div>
          ) : (
            <div className="text-center py-6"><Loader2 className="w-5 h-5 animate-spin mx-auto text-muted-foreground" /></div>
          )}
        </DialogContent>
      </Dialog>


      {/* Invoice Value Breakdown Popup */}
      <Dialog open={!!invoicePopup} onOpenChange={(open) => { if (!open) setInvoicePopup(null); }}>
        <DialogContent className="sm:max-w-[360px]" data-testid="invoice-breakdown-popup">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base" style={{ fontFamily: 'Manrope' }}>
              <IndianRupee className="w-5 h-5 text-green-600" />
              Invoice Breakdown
            </DialogTitle>
            <DialogDescription className="text-xs">{invoicePopup?.order_number}</DialogDescription>
          </DialogHeader>
          {invoicePopup && (
            <div className="space-y-3 mt-2">
              {/* Items summary */}
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="text-[10px] py-1.5 px-2 font-extrabold">Grade</TableHead>
                      <TableHead className="text-[10px] py-1.5 px-2 font-extrabold text-center">Qty</TableHead>
                      <TableHead className="text-[10px] py-1.5 px-2 font-extrabold text-right">Rate</TableHead>
                      <TableHead className="text-[10px] py-1.5 px-2 font-extrabold text-right">Amount</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {invoicePopup.items?.map((item, i) => (
                      <TableRow key={i}>
                        <TableCell className="text-xs py-1.5 px-2 font-semibold">{item.grade} {item.sku !== 'TIN' ? item.sku : ''}</TableCell>
                        <TableCell className="text-xs py-1.5 px-2 text-center">{item.qty}</TableCell>
                        <TableCell className="text-xs py-1.5 px-2 text-right">₹{item.rate?.toLocaleString('en-IN')}</TableCell>
                        <TableCell className="text-xs py-1.5 px-2 text-right font-semibold">₹{(item.amount || item.qty * item.rate)?.toLocaleString('en-IN')}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Value breakdown */}
              <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                <div className="flex justify-between text-xs">
                  <span className="text-muted-foreground">Order Value</span>
                  <span className="font-semibold">₹{(invoicePopup.total_value || 0).toLocaleString('en-IN')}</span>
                </div>
                {(invoicePopup.total_discount > 0) && (
                  <div className="flex justify-between text-xs">
                    <span className="text-red-600">Discount ({invoicePopup.discount_per_kg ? `₹${invoicePopup.discount_per_kg}/kg` : ''})</span>
                    <span className="font-semibold text-red-600">- ₹{(invoicePopup.total_discount || 0).toLocaleString('en-IN')}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm pt-1 border-t">
                  <span className="font-bold">Invoice Value</span>
                  <span className="font-bold text-green-700">₹{getOrderDisplayValue(invoicePopup).toLocaleString('en-IN')}</span>
                </div>
                {invoicePopup.payment_terms && (
                  <div className="flex justify-between text-[10px] text-muted-foreground">
                    <span>Payment Terms</span>
                    <span className="font-semibold">{invoicePopup.payment_terms}</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

        {/* Deals Tab — All deals in chronological order with filter buttons */}
        {activeTab === 'deals' && (() => {
          const acceptedDeals = orderRequests.filter(r => ['processing', 'confirmed', 'ordered'].includes(r.status));
          const rejectedDeals = orderRequests.filter(r => r.status === 'rejected');
          const cancelledDeals = orderRequests.filter(r => r.status === 'order_cancelled');
          
          // All deals sorted chronologically (newest first) by updated_at or created_at
          const allDealsSorted = [...acceptedDeals, ...rejectedDeals, ...cancelledDeals]
            .sort((a, b) => (b.updated_at || b.created_at || '').localeCompare(a.updated_at || a.created_at || ''));

          const getStampStyle = (status) => {
            if (['processing', 'confirmed', 'ordered'].includes(status))
              return { text: 'APPROVED', color: '#16a34a', border: '#16a34a', rotate: '-6deg' };
            if (status === 'rejected')
              return { text: 'REJECTED', color: '#dc2626', border: '#dc2626', rotate: '4deg' };
            return { text: 'CANCELLED', color: '#6b7280', border: '#6b7280', rotate: '-3deg' };
          };

          const handleReorder = async (reqId) => {
            try {
              const res = await axios.post(`${API}/order-requests/reorder`, { source_request_id: reqId });
              toast.success(res.data?.message || "New request created!");
              fetchOrderRequests();
            } catch (e) { toast.error(e.response?.data?.detail || "Failed to re-order"); }
          };

          const getRemark = (deal) => {
            const actionNeg = [...(deal.negotiations || [])].reverse().find(n => n.action === 'rejected' || n.action === 'order_cancelled' || n.action === 'sold_out' || n.action === 'auto_cancelled');
            return actionNeg?.notes || deal.reject_reason || '';
          };

          const filteredDeals = dealsFilter === 'all' ? allDealsSorted
            : dealsFilter === 'approved' ? allDealsSorted.filter(d => ['processing', 'confirmed', 'ordered'].includes(d.status))
            : dealsFilter === 'rejected' ? allDealsSorted.filter(d => d.status === 'rejected')
            : allDealsSorted.filter(d => d.status === 'order_cancelled');

          return (
            <div className="space-y-3 px-1" data-testid="deals-tab">
              {/* Filter buttons with counts */}
              <div className="flex gap-1.5 flex-wrap">
                <button onClick={() => setDealsFilter('all')}
                  className={`text-[10px] font-bold px-3 py-1.5 rounded-full border transition-colors ${dealsFilter === 'all' ? 'bg-gray-800 text-white border-gray-800' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
                  All ({allDealsSorted.length})
                </button>
                <button onClick={() => setDealsFilter('approved')}
                  className={`text-[10px] font-bold px-3 py-1.5 rounded-full border transition-colors ${dealsFilter === 'approved' ? 'bg-green-600 text-white border-green-600' : 'bg-green-50 border-green-200 text-green-800 hover:bg-green-100'}`}>
                  Approved ({acceptedDeals.length})
                </button>
                <button onClick={() => setDealsFilter('rejected')}
                  className={`text-[10px] font-bold px-3 py-1.5 rounded-full border transition-colors ${dealsFilter === 'rejected' ? 'bg-red-600 text-white border-red-600' : 'bg-red-50 border-red-200 text-red-800 hover:bg-red-100'}`}>
                  Rejected ({rejectedDeals.length})
                </button>
                <button onClick={() => setDealsFilter('cancelled')}
                  className={`text-[10px] font-bold px-3 py-1.5 rounded-full border transition-colors ${dealsFilter === 'cancelled' ? 'bg-gray-600 text-white border-gray-600' : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100'}`}>
                  Cancelled ({cancelledDeals.length})
                </button>
              </div>

              {filteredDeals.length > 0 ? filteredDeals.map(deal => {
                const items = deal.latest_items || [];
                const totalQty = items.reduce((s, i) => s + (i.qty || 0), 0);
                const stamp = getStampStyle(deal.status);
                const remark = getRemark(deal);
                return (
                  <div key={deal.id} className="relative border rounded-xl bg-white overflow-hidden shadow-sm" data-testid={`deal-card-${deal.id}`}>
                    {/* Rubber stamp overlay — centered */}
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none select-none z-10" style={{ transform: `rotate(${stamp.rotate})` }}>
                      <div style={{
                        border: `3px solid ${stamp.color}`,
                        borderRadius: '6px',
                        padding: '3px 14px',
                        color: stamp.color,
                        fontSize: '18px',
                        fontWeight: 900,
                        fontFamily: 'Impact, Arial Black, sans-serif',
                        letterSpacing: '3px',
                        opacity: 0.25,
                        textTransform: 'uppercase',
                        lineHeight: 1.2,
                        background: `repeating-linear-gradient(0deg, transparent, transparent 2px, ${stamp.color}10 2px, ${stamp.color}10 3px)`,
                      }}>
                        {stamp.text}
                      </div>
                    </div>

                    {/* Card header */}
                    <div className="flex items-center gap-2 px-3 py-2 border-b bg-gray-50/50">
                      <span className="text-[11px] font-mono font-bold">{deal.enquiry_number}</span>
                      {deal.order_number && <Badge variant="outline" className="text-[8px] font-mono px-1 py-0">{deal.order_number}</Badge>}
                      <span className="text-[9px] text-muted-foreground ml-auto">{formatIST(deal.updated_at || deal.created_at)}</span>
                    </div>

                    {/* Items */}
                    <div className="px-3 py-2 space-y-1.5">
                      <div className="flex flex-wrap gap-1">
                        {items.slice(0, 4).map((item, idx) => (
                          <span key={idx} className="bg-gray-100 px-1.5 py-0.5 rounded text-[9px]">{item.grade} x{item.qty} @₹{item.rate?.toLocaleString('en-IN')}</span>
                        ))}
                        {items.length > 4 && <span className="text-[9px] text-muted-foreground">+{items.length - 4} more</span>}
                      </div>

                      {/* Remark */}
                      {remark && (
                        <p className="text-[9px] text-red-600 bg-red-50 rounded px-2 py-1 italic">"{remark}"</p>
                      )}

                      {/* Footer */}
                      <div className="flex items-center justify-between pt-1">
                        <span className="text-[9px] text-muted-foreground">Qty: {totalQty} | {(deal.negotiations || []).filter(n => !n.action).length} rounds</span>
                        <Button size="sm" className="h-6 text-[9px] px-2.5 bg-emerald-600 hover:bg-emerald-700 gap-1 rounded-full"
                          onClick={() => {
                            // Auto-fill New Order form with deal's items
                            const dealItems = items.map(i => ({
                              grade: i.grade || '', sku: i.sku || 'BUCKET', qty: String(i.qty || ''), customer_rate: '', gradeOpen: false
                            }));
                            setItems(dealItems.length > 0 ? dealItems : [{ grade: "", sku: "BUCKET", qty: "", customer_rate: "", gradeOpen: false }]);
                            setActiveTab('new_order');
                            toast.success("Order pre-filled from deal — edit and submit!");
                          }} data-testid={`reorder-${deal.id}`}>
                          <Plus className="w-2.5 h-2.5" /> New Order Request
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              }) : filteredDeals.length === 0 && allDealsSorted.length > 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <p className="text-sm">No {dealsFilter} deals</p>
                </div>
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  <CheckCircle className="w-10 h-10 mx-auto mb-3 opacity-20" />
                  <p className="text-sm">No deals yet</p>
                </div>
              )}
            </div>
          );
        })()}

      {/* Dispatch Details Popup */}
      <Dialog open={!!dispatchPopup} onOpenChange={(open) => { if (!open) setDispatchPopup(null); }}>
        <DialogContent className="sm:max-w-[420px] max-h-[90vh] overflow-y-auto" data-testid="dispatch-details-popup">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base" style={{ fontFamily: 'Manrope' }}>
              <Truck className="w-5 h-5 text-emerald-600" />
              Dispatch Details
            </DialogTitle>
            <DialogDescription className="text-xs">
              {dispatchPopup?.order_number} — {dispatchPopup?.dispatch_number}
            </DialogDescription>
          </DialogHeader>
          {dispatchPopup && (
            <div className="space-y-3 mt-2">
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="bg-gray-50 rounded p-2">
                  <p className="text-[10px] text-muted-foreground mb-0.5">Order Number</p>
                  <p className="font-mono font-bold">{dispatchPopup.order_number}</p>
                </div>
                <div className="bg-gray-50 rounded p-2">
                  <p className="text-[10px] text-muted-foreground mb-0.5">Dispatch Number</p>
                  <div className="flex items-center gap-2">
                    <p className="font-mono font-bold text-emerald-700">{dispatchPopup.dispatch_number}</p>
                    {dispatchPopup.invoice_pdf_path && (
                      <button onClick={() => window.open(`${API}/dispatch/${dispatchPopup.dispatch_number}/invoice`, '_blank')}
                        className="text-blue-600 hover:text-blue-800 flex items-center gap-0.5 text-[9px] underline">
                        <FileText className="w-3 h-3" /> Invoice
                      </button>
                    )}
                  </div>
                </div>
                <div className="bg-gray-50 rounded p-2">
                  <p className="text-[10px] text-muted-foreground mb-0.5">Status</p>
                  <Badge className={`${getOrderStatusInfo(dispatchPopup).cls} text-[9px]`}>
                    {getOrderStatusInfo(dispatchPopup).label}
                  </Badge>
                </div>
                <div className="bg-gray-50 rounded p-2">
                  <p className="text-[10px] text-muted-foreground mb-0.5">Payment</p>
                  <p className="font-semibold">{dispatchPopup.payment_terms || 'N/A'}</p>
                </div>
              </div>

              {(dispatchPopup.transport_name || dispatchPopup.vehicle_number || dispatchPopup.lr_number) && (
                <div className="border rounded-lg p-3 bg-blue-50/50">
                  <p className="text-[10px] font-bold text-blue-800 mb-2 flex items-center gap-1">
                    <Truck className="w-3 h-3" /> Transport Details
                  </p>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    {dispatchPopup.transport_name && (
                      <div><p className="text-[9px] text-muted-foreground">Transport</p><p className="font-medium">{dispatchPopup.transport_name}</p></div>
                    )}
                    {dispatchPopup.vehicle_number && (
                      <div><p className="text-[9px] text-muted-foreground">Vehicle</p><p className="font-medium">{dispatchPopup.vehicle_number}</p></div>
                    )}
                    {dispatchPopup.lr_number && (
                      <div><p className="text-[9px] text-muted-foreground">LR Number</p><p className="font-medium">{dispatchPopup.lr_number}</p></div>
                    )}
                    {dispatchPopup.dispatched_at && (
                      <div><p className="text-[9px] text-muted-foreground">Dispatched At</p><p className="font-medium">{dispatchPopup.dispatched_at?.slice(0, 16).replace('T', ' ')}</p></div>
                    )}
                  </div>
                </div>
              )}

              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-emerald-50">
                      <TableHead className="text-[10px] py-1.5 px-2 font-extrabold">Grade</TableHead>
                      <TableHead className="text-[10px] py-1.5 px-2 font-extrabold">SKU</TableHead>
                      <TableHead className="text-[10px] py-1.5 px-2 font-extrabold text-center">Qty</TableHead>
                      <TableHead className="text-[10px] py-1.5 px-2 font-extrabold text-right">Rate</TableHead>
                      <TableHead className="text-[10px] py-1.5 px-2 font-extrabold text-right">Amount</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {dispatchPopup.items?.map((item, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="text-xs py-1.5 px-2 font-semibold">{item.grade}</TableCell>
                        <TableCell className="text-xs py-1.5 px-2">{item.sku}</TableCell>
                        <TableCell className="text-xs py-1.5 px-2 text-center font-medium">{item.qty}</TableCell>
                        <TableCell className="text-xs py-1.5 px-2 text-right">₹{item.rate?.toLocaleString('en-IN')}</TableCell>
                        <TableCell className="text-xs py-1.5 px-2 text-right font-semibold text-green-700">
                          ₹{(item.amount || (item.qty * item.rate))?.toLocaleString('en-IN')}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow className="bg-gray-50 font-bold">
                      <TableCell colSpan={2} className="text-xs py-1.5 px-2 text-right">Total</TableCell>
                      <TableCell className="text-xs py-1.5 px-2 text-center font-bold">{dispatchPopup.total_qty}</TableCell>
                      <TableCell className="text-xs py-1.5 px-2"></TableCell>
                      <TableCell className="text-xs py-1.5 px-2 text-right font-bold text-green-700">
                        ₹{(dispatchPopup.total_value || 0).toLocaleString('en-IN')}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>

              {dispatchPopup.invoice_number && (
                <div className="bg-amber-50 rounded-lg p-2 text-xs flex items-center gap-2">
                  <Hash className="w-3.5 h-3.5 text-amber-600" />
                  <span className="font-medium text-amber-800">Invoice: {dispatchPopup.invoice_number}</span>
                </div>
              )}

              {/* Value breakdown with discount */}
              <div className="bg-gray-50 rounded-lg p-3 space-y-1.5">
                <div className="flex justify-between text-xs">
                  <span className="text-muted-foreground">Total Value</span>
                  <span className="font-semibold">₹{(dispatchPopup.total_value || 0).toLocaleString('en-IN')}</span>
                </div>
                {dispatchPopup.total_discount > 0 && (
                  <div className="flex justify-between text-xs">
                    <span className="text-red-600">Discount{dispatchPopup.discount_per_kg ? ` (₹${dispatchPopup.discount_per_kg}/kg)` : ''}</span>
                    <span className="font-semibold text-red-600">- ₹{(dispatchPopup.total_discount || 0).toLocaleString('en-IN')}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm pt-1 border-t">
                  <span className="font-bold">Invoice Value</span>
                  <span className="font-bold text-green-700">₹{getOrderDisplayValue(dispatchPopup).toLocaleString('en-IN')}</span>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}


const inrC = (n) => `₹${Math.round(n || 0).toLocaleString("en-IN")}`;

function CustomerComboOffer({ customerName, customerId, onSubmitted, ownerView }) {
  const [config, setConfig] = useState(null);
  const [qty, setQty] = useState("");
  const [step, setStep] = useState("qty"); // qty | grades | offer
  const [gradeOptions, setGradeOptions] = useState([]);
  const [allowed, setAllowed] = useState(0);
  const [chosen, setChosen] = useState([]);
  const [lines, setLines] = useState(null);
  const [target, setTarget] = useState(0);
  const [avg, setAvg] = useState(0);
  const [limit, setLimit] = useState(2);
  const [shufflesUsed, setShufflesUsed] = useState(0);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    (async () => {
      if (!customerName) return;
      try {
        const res = await axios.get(`${API}/customer/combo-config?customer_name=${encodeURIComponent(customerName)}`);
        setConfig(res.data);
      } catch (e) { setConfig({ enabled: false }); }
    })();
  }, [customerName]);

  if (!config || !config.enabled) return null;

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} data-testid="customer-combo-open"
        className="w-full mb-4 flex items-center gap-3 rounded-xl border border-emerald-200 bg-gradient-to-r from-emerald-50 to-white px-4 py-3 hover:shadow-md transition-shadow text-left">
        <div className="w-9 h-9 rounded-full bg-emerald-600 flex items-center justify-center shrink-0">
          <Gift className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1">
          <div className="text-sm font-bold text-emerald-800">Get a Special Combo Offer</div>
          <div className="text-[11px] text-emerald-700">Tell us your quantity — we'll build a smart, best-value mix for you.</div>
        </div>
        <ArrowRight className="w-4 h-4 text-emerald-600" />
      </button>
    );
  }

  const safe = (n) => (n === "" || isNaN(n) ? 0 : Number(n));
  const totalUnits = lines ? lines.reduce((s, l) => s + safe(l.qty), 0) : 0;
  const totalValue = lines ? lines.reduce((s, l) => s + safe(l.qty) * safe(l.rate), 0) : 0;

  const nextToGrades = async () => {
    if (!qty || parseFloat(qty) <= 0) { toast.error("Enter the quantity you're looking for"); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/customer/combo-grades`, { customer_name: customerName, total_qty: parseFloat(qty) });
      const opts = res.data.grades || [];
      const allow = res.data.allowed || 0;
      setGradeOptions(opts); setAllowed(allow); setChosen([]);
      if (allow <= 0) { await generate([]); }
      else setStep("grades");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start combo");
    } finally { setLoading(false); }
  };

  const toggleChoice = (key) => {
    setChosen((prev) => {
      if (prev.includes(key)) return prev.filter((k) => k !== key);
      if (prev.length >= allowed) { toast.error(`You can pick up to ${allowed} grade(s)`); return prev; }
      return [...prev, key];
    });
  };

  const generate = async (preferred) => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/customer/combo-offer`, {
        customer_name: customerName, total_qty: parseFloat(qty), preferred: preferred ?? chosen,
      });
      setLines((res.data.lines || []).map((l) => ({ ...l, baseRate: l.market_rate || l.rate })));
      setTarget(res.data.target_avg || 0);
      setAvg(res.data.summary?.avg_price_per_kg || 0);
      setLimit(res.data.shuffle_limit || 2);
      setShufflesUsed(0);
      setStep("offer");
      toast.success("Your combo offer is ready!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not generate a combo offer");
    } finally { setLoading(false); }
  };

  const shuffle = () => {
    if (!lines || shufflesUsed >= limit) return;
    const rnd = (a, b) => a + Math.random() * (b - a);
    let ls = lines.map((l) => {
      let q = Math.round(safe(l.qty) * rnd(0.7, 1.3));
      if (l.source === "high-stock") q = Math.max(2, q - (q % 2)); else q = Math.max(1, q);
      if (l.stock) q = Math.min(q, Math.floor(l.stock));
      let b = Math.round(safe(l.baseRate) * rnd(0.975, 1.01));
      return { ...l, qty: q, baseRate: b };
    });
    // reapply: preferred grades -2% of market base, others scaled to hold target
    const totalKg = ls.reduce((s, l) => s + l.qty * (l.pack_kg || 0), 0);
    const targetValue = (target || 0) * totalKg;
    const prefValue = ls.filter((l) => l.preferred).reduce((s, l) => s + l.qty * Math.round(l.baseRate * 0.98), 0);
    const otherBase = ls.filter((l) => !l.preferred).reduce((s, l) => s + l.qty * l.baseRate, 0);
    const scale = otherBase > 0 ? (targetValue - prefValue) / otherBase : 1;
    ls = ls.map((l) => ({ ...l, market_rate: l.baseRate, rate: l.preferred ? Math.round(l.baseRate * 0.98) : Math.round(l.baseRate * scale) }));
    const val = ls.reduce((s, l) => s + l.qty * l.rate, 0);
    setLines(ls);
    setAvg(totalKg ? Math.round((val / totalKg) * 10) / 10 : 0);
    setShufflesUsed((n) => n + 1);
    toast.success("Fresh combo — quantities & rates reshuffled");
  };

  const submit = async () => {
    if (!lines || lines.length === 0) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/customer/order-request`, {
        customer_name: customerName,
        customer_id: customerId,
        items: lines.filter((l) => safe(l.qty) > 0).map((l) => ({
          grade: l.grade, sku: l.sku, qty: parseInt(safe(l.qty)),
          customer_rate: safe(l.rate), suggested_rate: safe(l.rate),
        })),
        notes: `Special Combo Offer — Avg ₹${avg}/kg`,
      });
      toast.success("Order request submitted!");
      setLines(null); setQty(""); setStep("qty"); setChosen([]);
      if (onSubmitted) onSubmitted();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to submit");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="mb-4 rounded-xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-white overflow-hidden" data-testid="customer-combo-section">
      <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-emerald-100">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-full bg-emerald-600 flex items-center justify-center">
            <Gift className="w-4 h-4 text-white" />
          </div>
          <div>
            <div className="text-sm font-bold text-emerald-800">Special Combo Offer</div>
            <div className="text-[11px] text-emerald-700">Tell us how much you need — we'll build a smart mix for you.</div>
          </div>
        </div>
        {ownerView && step === "offer" && (
          <div className="text-right" data-testid="customer-combo-avg">
            <div className="text-[9px] text-gray-500 uppercase">Avg / Kg (internal)</div>
            <div className="text-sm font-bold text-emerald-800">{inrC(avg)}</div>
          </div>
        )}
      </div>

      <div className="p-4 space-y-3">
        {/* Step 1: qty */}
        {step === "qty" && (
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <Label className="text-xs">Quantity you're looking for (units)</Label>
              <Input type="number" value={qty} onChange={(e) => setQty(e.target.value)}
                placeholder="e.g. 50" className="h-9 mt-1 tabular-nums" data-testid="customer-combo-qty" />
            </div>
            <Button onClick={nextToGrades} disabled={loading} className="h-9 gap-1.5 bg-emerald-600 hover:bg-emerald-700" data-testid="customer-combo-next">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
              Next
            </Button>
          </div>
        )}

        {/* Step 2: choose grades */}
        {step === "grades" && (
          <div className="space-y-3" data-testid="customer-combo-grade-step">
            <p className="text-xs text-emerald-800 font-medium">
              Pick up to <b>{allowed}</b> grade(s) you specially want at a lower price ({chosen.length}/{allowed}). Or skip to let us choose.
            </p>
            <div className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto">
              {gradeOptions.map((g) => {
                const key = `${g.grade}|${g.sku}`;
                const on = chosen.includes(key);
                return (
                  <button key={key} onClick={() => toggleChoice(key)} data-testid={`customer-combo-choice-${g.grade}-${g.sku}`}
                    className={`text-[11px] px-2 py-1 rounded-full border ${on ? "bg-amber-400 border-amber-500 text-amber-950 font-semibold" : "bg-white border-gray-300 text-gray-700"}`}>
                    {on && <Star className="w-3 h-3 inline mr-0.5 fill-amber-700" />}{g.grade} {(g.sku || "").replace("_", " ")}
                  </button>
                );
              })}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => generate([])} disabled={loading} className="flex-1 h-9" data-testid="customer-combo-skip">Skip</Button>
              <Button onClick={() => generate(chosen)} disabled={loading} className="flex-1 h-9 gap-1.5 bg-emerald-600 hover:bg-emerald-700" data-testid="customer-combo-generate">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                Generate Combo
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: offer */}
        {step === "offer" && lines && (
          <div className="space-y-3">
            <div className="bg-white rounded-lg border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="text-[10px] py-2 px-2 font-extrabold">Grade</TableHead>
                    <TableHead className="text-[10px] py-2 px-2 font-extrabold">SKU</TableHead>
                    <TableHead className="text-[10px] py-2 px-2 font-extrabold text-center">Qty</TableHead>
                    <TableHead className="text-[10px] py-2 px-2 font-extrabold text-right">Rate</TableHead>
                    <TableHead className="text-[10px] py-2 px-2 font-extrabold text-right">Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {lines.map((l, i) => {
                    const belowMarket = ownerView && l.market_rate && safe(l.rate) < l.market_rate * 0.99;
                    return (
                    <TableRow key={i} data-testid={`customer-combo-line-${l.grade}-${l.sku}`} className={belowMarket ? "bg-amber-100" : (l.preferred ? "bg-amber-50" : "")}>
                      <TableCell className="py-1.5 px-2 text-xs font-semibold">{l.grade}{l.preferred && <Star className="w-3 h-3 inline ml-1 fill-amber-400 text-amber-500" />}</TableCell>
                      <TableCell className="py-1.5 px-2 text-xs">{(l.sku || "").replace("_", " ")}</TableCell>
                      <TableCell className="py-1.5 px-2 text-xs text-center tabular-nums">{l.qty}</TableCell>
                      <TableCell className="py-1.5 px-2 text-xs text-right tabular-nums">
                        {inrC(l.rate)}
                        {ownerView && l.market_rate ? <span className="block text-[9px] text-gray-400">mkt {inrC(l.market_rate)}</span> : null}
                      </TableCell>
                      <TableCell className="py-1.5 px-2 text-xs text-right tabular-nums font-medium">{inrC(safe(l.qty) * safe(l.rate))}</TableCell>
                    </TableRow>
                    );
                  })}
                  <TableRow className="bg-emerald-50 font-bold">
                    <TableCell className="py-2 px-2 text-xs" colSpan={2}>Total</TableCell>
                    <TableCell className="py-2 px-2 text-xs text-center tabular-nums">{Math.round(totalUnits)}</TableCell>
                    <TableCell className="py-2 px-2"></TableCell>
                    <TableCell className="py-2 px-2 text-xs text-right tabular-nums text-emerald-800" data-testid="customer-combo-total">{inrC(totalValue)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>

            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={shuffle} disabled={shufflesUsed >= limit}
                className="flex-1 h-9 gap-1.5" data-testid="customer-combo-shuffle">
                <Shuffle className="w-4 h-4" />
                {shufflesUsed >= limit ? "No shuffles left" : `Shuffle (${limit - shufflesUsed} left)`}
              </Button>
              <Button onClick={submit} disabled={submitting}
                className="flex-1 h-9 gap-1.5 bg-emerald-600 hover:bg-emerald-700" data-testid="customer-combo-submit">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Submit Order Request
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

